import { PHISHING_USER_STATUS } from '../../domain/domainCodes.js'
/**
 * English mirror of ./strings.ar.js — GENERATED by scripts/i18n/build.mjs.
 *
 * Wording follows the legacy Locale/*.resx English resources wherever the
 * legacy screen had the same label. To change any of it, edit
 * scripts/i18n/strings.en.json and re-run the script.
 */

const shared = {
  editor: 'Editor',
  addNew: 'Add New',
  export: 'Download',
  import: 'Import',
  close: 'Close',
  active: 'Active',
  inactive: 'Disabled',
  edit: 'Update',
  delete: 'Delete',
}

export const templatesStrings = {
  actionFailed: 'The operation could not be completed',
  /* Sorting reorders the rows on this page. The list endpoint orders by last update
     and pages before it orders, so it cannot be asked for a differently ordered page. */
  sortScope: 'Sorting applies to the templates on this page.',
  imported: 'Templates imported',
  importNote: 'The archive is what this screen exports: one folder per template, each holding details.json. A template whose name already exists is not imported.',
  ...shared,
  title: 'Phishing Templates',

  columns: {
    name: 'Name',
    language: 'Language',

    difficulty: 'Level',
    payloadType: 'Content',
    status: 'Active',

    emailImage: 'The email',
    landingImage: 'Landing Page',
    afterImage: 'After the catch',
  },

  templateImages: 'Template Images',

  surfaces: {
    email: 'Email Thumbnail',
    landing: 'Landing Page Thumbnail',
    after: 'After Phishing Page Thumbnail',
  },

  surfaceTabs: { email: 'The email', landing: 'Landing Page', after: 'After the catch' },

  surfaceNote: 'Preview drawn from the template\'s own data; the uploaded image replaces it once saved.',

  languages: {
    ar: 'AR',
    en: 'EN',
    arFull: 'Arabic Text',
    enFull: 'English Text',
  },

  form: {
    title: 'Phishing template',
    category: 'Category',

    categoryPlaceholder: 'Choose a category',
    save: 'Save',
    saved: 'Template saved',
    required: 'Field required',

    steps: {
      identity: 'Template details',
      email: 'The email',
      landing: 'Landing Page',
      after: 'After the catch',
    },
    stepHints: {
      identity: 'Category, name, level and language',
      email: 'Subject, content and attachments',
      landing: 'Where a click on the link lands',
      after: 'What they see once they are caught',
    },

    subject: 'Subject',
    thumbnail: 'Content Thumbnail',
    body: 'Body',
    richText: 'Rich Text',
    attachments: 'Upload Email Attachments',
    trackingImage: 'Tracking Image',
    landingPage: 'Landing Page',
    captureData: 'Capture Submitted Data',
    capturePasswords: 'Capture Passwords',
    redirectToUrl: 'Redirect To Url',
    redirectUrl: 'Redirect Url',
    redirectPage: 'Post-Caught Content',
    isActive: 'Active',
  },

  difficulties: { 0: 'Easy', 1: 'Medium', 2: 'Hard' },

  payloads: { 0: 'Website', 1: 'Attachment', 2: 'Information Request' },

  content: {
    title: 'Template content',
    subject: 'Message Title',
    tabs: { compose: 'Compose', source: 'HTML', preview: 'Preview' },
    tokens: 'Merge fields',
    tokenHint: 'These fields are replaced with the recipient\'s details on send.',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    heading: 'Heading',
    bullets: 'List',
    link: 'Link',
    linkPrompt: 'URL',
    clear: 'Clear formatting',
    save: 'Save',
    saved: 'Operation Completed Successfully',
  },

  tokenList: [
    { token: '{{name}}', label: 'Recipient name' },
    { token: '{{email}}', label: 'Email' },
    { token: '{{department}}', label: 'Group' },
    { token: '{{link}}', label: 'Campaign link' },
    { token: '{{date}}', label: 'Date' },
  ],

  deleteConfirm: 'Delete this template?',

  deleted: 'Operation Completed Successfully',
}

export const campaignsStrings = {
  ...shared,
  title: 'Phishing Campaigns',
  importTitle: 'Import old phishing system data',
  importHint: 'Upload .db File',
  from: 'From',
  to: 'To',
  report: 'Report',

  saved: 'Campaign saved',
  copied: 'Campaign copied',
  actionFailed: 'The operation could not be completed',

  /** No status is stored — these four are derived from active / started / finished. */
  states: {
    scheduled: 'Scheduled',
    running: 'Running',
    finished: 'Finished',
    inactive: 'Disabled',
  },

  disableTitle: 'Stop this campaign?',
  activateTitle: 'Start this campaign?',
  disableConfirm: 'It will stop sending, and its results will no longer be counted.',
  activateConfirm: 'It will run again on its own dates.',
  deleteWarning: 'Its recipient list and every recorded result go with it. This cannot be undone.',

  columns: {
    name: 'Name',
    period: 'Period',
    sent: 'Email Sent',
    opened: 'Email Opened',
    clicked: 'Link Clicked',
    submitted: 'Data Submitted',
    reported: 'Reported',
    status: 'Status',
  },

  row: {
    report: 'Phishing Report',
    notifications: 'Notifications',
    copy: 'Clone',
    activate: 'Activate',
    disable: 'Disable',
  },

  form: {
    message: 'Message and destination',
    timing: 'Timing',
    audience: 'Audience',
    templatePlaceholder: 'Choose a template',
    urlPlaceholder: 'Choose a link',
    senderPlaceholder: 'Choose a sender address',
    programPlaceholder: 'No automatic enrolment',
    name: 'Name',
    template: 'Campaign Template',
    url: 'URL',
    sender: 'Sender Email',
    startDate: 'Start Date',
    endDate: 'End Date',
    schedule: 'Send Emails By',
    groups: 'Departments',
    selectAll: 'Select All',
    filter: 'Filter',
    program: 'Learning Program Auto Enroll',
    active: 'Active',
    save: 'Save',
    required: 'Field required',
    schedulingHint: 'Sets when the campaign\'s messages go out. Leave it empty to send them as soon as the campaign starts.',
  },

  deleteConfirm: 'Delete this campaign?',

  deleted: 'Operation Completed Successfully',
}

export const phishingSettingsStrings = {
  ...shared,
  title: 'Settings',
  subtitle: 'Phishing',

  actionFailed: 'The operation could not be completed',
  saved: 'Saved',
  save: 'Save',
  required: 'Required',

  /** The settings tab — the loose settings the legacy keeps under phishing settings. */
  settings: {
    engine: 'Phishing engine',
    engineHint: 'Sends phishing campaigns and records what recipients do',
    intervalHint: 'As hours:minutes:seconds — for example 00:05:00',
    phishingEnabled: 'Enable phishing',
    phishingInterval: 'Phishing service interval',
    listener: 'Report mailbox listener',
    listenerHint: 'Reads the mailbox users report suspicious messages to',
    listenerEnabled: 'Enable the listener',
    listenerInterval: 'Listener service interval',
    url: 'Phishing service URL',
    urlHint: 'The address phishing pages are served from and events are counted against',
    skipHeaders: 'Ignored headers',
    skipHeadersHint: 'Requests carrying any of these are not counted — used to ignore security scanners',
    skipHeadersAdd: 'Add a header',
    skipHeadersEmpty: 'No ignored headers',
    outlook: 'Outlook add-in',
    outlookHint: 'How a user reports a suspicious message from inside Outlook',
    outlookType: 'Add-in type',
    outlookMethod: 'Reporting method',
    outlookEmail: 'Reports mailbox',
    outlookDelete: 'Delete the message after reporting',
    outlookNote: 'The reporting method and delete-after-report cannot be cleared once set — the API skips an empty value.',
  },

  categoryForm: {
    add: 'New category',
    edit: 'Edit category',
    enabled: 'This category is active',
    empty: 'No categories yet',
    deleteConfirm: 'Delete this category?',
  },

  domainForm: {
    add: 'New link',
    edit: 'Edit link',
    empty: 'No links yet',
    deleteConfirm: 'Delete this link?',
    imported: 'Links imported',
    importNote: 'An Excel file holding each link name and its address.',
  },

  /** The sender profiles a campaign sends from. */
  smtpForm: {
    add: 'New sender',
    edit: 'Edit sender',
    empty: 'No senders yet',
    deleteConfirm: 'Delete this sender?',
    imported: 'Senders imported',
    importNote:
      'An Excel file with the columns: name, sender email, host, port, username, password, connection method, active, setting name, visible sender address, validate server certificate. Rows are matched by name, and an empty password keeps the stored one.',

    name: 'Sender name',
    nameHint: 'The name recipients see in the From field',
    settingName: 'Setting name',
    settingNameHint: 'An internal name to tell settings apart; recipients never see it',
    from: 'Sender email',
    fromHint: 'The address actually used to send (MAIL FROM)',
    visibleSenderAddress: 'Visible sender address',
    visibleSenderAddressHint: 'What recipients read — leave empty to use the sender email',

    host: 'Mail host',
    hostHint: 'The sending server, e.g. mail.domain.com',
    port: 'Port',
    portHint: 'Usually 587 or 465',
    username: 'Username',
    usernameHint: 'The mailbox used to sign in to the server',
    password: 'Password',
    passwordHint: 'The mailbox password',
    passwordKeptHint: 'Leave empty to keep the stored password',

    connectionMethod: 'Connection method',
    connectionMethodHint: 'The encryption used for the connection',
    validateCertificate: 'Validate server certificate',
    validateCertificateHint: 'Turn validation off for a server with a self-signed certificate',
    certificateDefault: 'Server default',
    certificateValidate: 'Validate the certificate',
    certificateSkip: 'Do not validate the certificate',
    active: 'This sender is active',
    activeHint: 'Only active senders are offered when a campaign picks one',

    test: 'Test sending',
    testAddress: 'Email address for testing',
  },

  /** The mailbox reports are read from. */
  imapForm: {
    add: 'New mailbox',
    edit: 'Edit mailbox',
    empty: 'No mailboxes yet',
    deleteConfirm: 'Delete this mailbox?',

    host: 'Mail host',
    hostHint: 'The mailbox server, e.g. mail.domain.com',
    port: 'Port',
    portHint: 'Usually 993 encrypted, 143 otherwise',
    username: 'Username',
    password: 'Password',
    passwordHint: 'The mailbox password',
    passwordKeptHint: 'Leave empty to keep the stored password',
    folderName: 'Folder name',
    folderNameHint: 'The folder reports are read from, e.g. INBOX',
    connectionMethod: 'Connection method',
    connectionMethodHint: 'The encryption used for the connection',

    tls: 'TLS',
    tlsHint: 'Applied together with the chosen connection method',
    deleteEmails: 'Delete campaign emails once read',
    deleteEmailsHint: 'A reported message is removed from the mailbox after it is counted',
    active: 'This mailbox is active',
    activeHint: 'The listener service reads the active mailbox',
    singleActiveNote: 'Only one mailbox can be active — activating this one deactivates the rest.',

    test: 'Test connection',
  },

  tabs: {
    settings: 'Phishing settings',
    categories: 'Phishing Categories',
    smtp: 'Email SMTP Settings',
    imap: 'Email IMAP Settings',
    domains: 'Domains',
  },

  categories: {
    name: 'Arabic Name',
    nameEn: 'English Name',
    status: 'Active',
  },

  smtp: {
    senderName: 'Sender Name',
    host: 'Host',
    port: 'Port',
    from: 'Sender Email',
    username: 'Username',
    status: 'Active',
  },

  imap: {
    host: 'Host',
    port: 'Port',
    username: 'Username',
    folderName: 'Folder Name',
    connectionMethod: 'Connection Method',
    deleteEmails: 'Delete Campaigns Emails',
    status: 'Active',
    lastSync: 'Last read',
    neverSynced: 'Never read',
  },

  domains: {
    name: 'Name',
    url: 'URL',
  },

  deleteConfirm: 'Delete this entry?',

  deleted: 'Operation Completed Successfully',
}

/** Phishing reports — both levels. */
export const reportStrings = {
  title: 'Phishing reports',
  subtitle: 'What the targets of each campaign did',
  funnel: 'Funnel',
  share: 'Shares',
  shareHint: 'Of everyone the campaign reached',
  byCampaign: 'By campaign',
  backToAll: 'All campaigns',
  exportExcel: 'Export to Excel',
  actionFailed: 'The operation could not be completed',
  noCampaigns: 'No campaigns',
  noUsers: 'This campaign reached nobody',

  /* Said plainly because the field names mislead. */
  countingNote:
    'Sent counts everyone enrolled in the campaign, including those not yet sent to. Opened is cumulative and includes everyone past it. Clicked excludes anyone who went on to report the message. Reported is driven by the reporting date, not by the status.',

  stages: {
    sent: 'Sent',
    opened: 'Opened',
    clicked: 'Clicked',
    submitted: 'Submitted data',
    reported: 'Reported',
  },

  columns: {
    campaign: 'Campaign',
    startedOn: 'Start date',
    status: 'Status',
    user: 'Target',
    email: 'Email',
    department: 'Department',
    phished: 'Times data submitted',
    reportedOn: 'Reported on',
  },

  /** PhishingUserStatus — the scale the recipient rows arrive on, -1..4. */
  states: {
    '-1': 'Scheduled',
    [PHISHING_USER_STATUS.SENT]: 'Sent',
    [PHISHING_USER_STATUS.EMAIL_OPENED]: 'Opened',
    [PHISHING_USER_STATUS.LINK_CLICKED]: 'Clicked',
    [PHISHING_USER_STATUS.DATA_SUBMITTED]: 'Submitted data',
    [PHISHING_USER_STATUS.USER_REPORTED]: 'Reported',
  },
}
